import React from 'react';
import {
  Alert, Badge, Button, EmptyState, ErrorState, NumberInput, Pager, Select, Spinner, Table,
} from '@parsina/framework';
import { ArrowLeft, DollarSign, FlaskConical, History } from 'lucide-react';

import { useTranslation, LTR_TEXT, MIRROR_ICON } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { EMPTY } from '../../lib/format';
import { FX_PAGE_SIZE, num, pageCountOf } from '../../lib/aiPricingView';
import { FX_RATES_QUERY, SERVICE_LABELS_QUERY, SIMULATE_NIGHTLY_FX } from './queries';
import {
  Code, Fact, MarginBadge, ParsehCell, PercentCell, TableSkeleton, TomanCell, useEnumLabel,
  useServiceLabel,
} from './shared';

const OUTCOME_VARIANT = {
  repriced: 'success', recorded: 'success', unchanged: 'outline', held: 'warning',
  rejected: 'destructive', no_policy: 'warning', disabled: 'outline',
};
const STATUS_VARIANT = { accepted: 'success', rejected: 'destructive' };

/**
 * «نرخ دلار شبانه» — the nightly dollar, inside the policies view (PM-0427).
 *
 * Every night the AI service reads the free-market dollar, adds the premium
 * it costs to buy provider credit from Iran, and — when the rate moved enough
 * and no guard trips — reprices the charging policy from the operator's own.
 * This section is the operator's window onto that:
 *
 *  · «شبیه‌سازی امشب» asks what tonight's run would do RIGHT NOW: the rate
 *    check, the rates, the price changes, and why it would hold. It writes
 *    nothing — the mutation is a mutation only because reading the market is
 *    a deliberate act, and it can take up to a minute when it does, so the
 *    button stays pending for as long as the answer takes.
 *  · the history lists every night, accepted or rejected, newest first.
 */
export default function NightlyFxSection({ policies, window, go }) {
  const { t } = useTranslation();
  return (
    <section className="mt-6 rounded-lg border border-border bg-card" aria-labelledby="ai-fx-title" data-testid="ai-fx">
      <div className="border-b border-border px-4 py-3">
        <h2 id="ai-fx-title" className="inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
          <DollarSign size={16} aria-hidden />
          {t('aiPricing.fx.title')}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('aiPricing.fx.sub')}</p>
      </div>
      <div className="flex flex-col gap-6 p-4">
        <Simulation policies={policies} window={window} go={go} />
        <FxHistory go={go} />
      </div>
    </section>
  );
}

function Simulation({ policies, window, go }) {
  const { t } = useTranslation();
  const statusLabel = useEnumLabel('aiPricing.policies.status');
  const [policyId, setPolicyId] = React.useState('');
  const [market, setMarket] = React.useState('');
  const [pending, setPending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [labels, setLabels] = React.useState([]);

  // The price changes carry service keys only; their names are a convenience.
  React.useEffect(() => {
    if (!result || labels.length) return undefined;
    let alive = true;
    gql(SERVICE_LABELS_QUERY, { window })
      .then((d) => { if (alive) setLabels(d?.aiServices?.services || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [result, labels.length, window]);

  const marketValid = market === '' || (num(market) != null && num(market) > 0);

  const run = async () => {
    if (pending || !marketValid) return;
    setPending(true);
    setError(null);
    try {
      const d = await gql(SIMULATE_NIGHTLY_FX, { policyId: policyId || null, marketToman: market === '' ? null : String(market) });
      setResult(d?.simulateAiNightlyFx || null);
    } catch (e) {
      setError(errorMessage(e, t));
    } finally {
      setPending(false);
    }
  };

  const choices = (policies || []).filter((p) => p.status !== 'archived');
  const state = pending ? 'loading' : error ? 'error' : result ? 'content' : 'idle';

  return (
    <div data-testid="ai-fx-sim" data-state={state}>
      <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
        <FlaskConical size={15} aria-hidden />
        {t('aiPricing.fx.sim.title')}
      </h3>
      <form
        className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
        onSubmit={(e) => { e.preventDefault(); run(); }}
        aria-label={t('aiPricing.fx.sim.title')}
      >
        <Select
          label={t('aiPricing.fx.sim.policy')}
          value={policyId}
          onChange={(e) => setPolicyId(e.target?.value ?? e)}
          options={[
            { value: '', label: t('aiPricing.fx.sim.policyActive') },
            ...choices.map((p) => ({ value: String(p.id), label: t('aiPricing.visits.filters.rateOption', { name: p.name, status: statusLabel(p.status) }) })),
          ]}
          placeholder={t('aiPricing.fx.sim.policyActive')}
          disabled={pending}
          data-testid="ai-fx-sim-policy"
        />
        <NumberInput
          label={t('aiPricing.fx.sim.market')}
          helperText={t('aiPricing.fx.sim.marketHelp')}
          value={market}
          onChange={(e) => setMarket(e.target?.value ?? e)}
          unit={t('common.units.toman')}
          min={1}
          error={marketValid ? undefined : t('aiPricing.fx.sim.marketInvalid')}
          disabled={pending}
          data-testid="ai-fx-sim-market"
        />
        <Button type="submit" variant="outline" disabled={pending || !marketValid} aria-busy={pending || undefined} data-testid="ai-fx-sim-run">
          {pending ? <Spinner size={16} thickness={2} color="currentColor" /> : <FlaskConical size={16} aria-hidden />}
          {t('aiPricing.fx.sim.run')}
        </Button>
      </form>
      <p className="mt-2 text-xs text-muted-foreground">{t('aiPricing.fx.sim.readOnly')}</p>

      {pending ? (
        <p className="mt-3 text-xs text-muted-foreground" role="status" aria-live="polite" data-testid="ai-fx-sim-pending">
          {t(market === '' ? 'aiPricing.fx.sim.pendingMarket' : 'aiPricing.fx.sim.pending')}
        </p>
      ) : error ? (
        <Alert
          variant="error"
          className="mt-3"
          action={<Button variant="outline" size="sm" onClick={run}>{t('common.actions.retry')}</Button>}
          data-testid="ai-fx-sim-error"
        >
          {error}
        </Alert>
      ) : result ? (
        <SimulationResult result={result} labels={labels} go={go} />
      ) : null}
    </div>
  );
}

function SimulationResult({ result: r, labels, go }) {
  const { t, has, formatDigits, formatNumber } = useTranslation();
  const pct = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 2 }));
  const verdictKey = `aiPricing.fx.sim.verdict.${r.outcome}`;
  const outcomeLabel = useEnumLabel('aiPricing.fx.outcome');
  const statusLabel = useEnumLabel('aiPricing.fx.status');
  const sourceLabel = useEnumLabel('aiPricing.fx.source');
  const unitLabel = useEnumLabel('aiPricing.fx.unit');
  const serviceLabel = useServiceLabel();
  const labelOf = (key) => {
    const row = labels.find((s) => s.serviceKey === key);
    return row ? serviceLabel(row) : key;
  };
  const rate = (v) => (num(v) == null ? EMPTY : <span className="whitespace-nowrap"><TomanCell value={v} /> {t('common.units.toman')}</span>);
  const policyLink = (id) => (id ? (
    <Button variant="link" size="sm" className="h-auto p-0" onClick={() => go({ policy: String(id) })} data-testid={`ai-fx-sim-policy-${id}`}>
      {t('aiPricing.services.source.policy', { id: formatDigits(id) })}
    </Button>
  ) : EMPTY);

  return (
    <div className="mt-4 flex flex-col gap-4" data-testid="ai-fx-sim-result" data-outcome={r.outcome}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={OUTCOME_VARIANT[r.outcome] || 'outline'} data-testid="ai-fx-sim-outcome">{outcomeLabel(r.outcome)}</Badge>
        {has && has(verdictKey) ? <span className="text-sm text-foreground">{t(verdictKey)}</span> : null}
      </div>

      <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4" data-testid="ai-fx-sim-rates">
        <Fact
          label={t('aiPricing.fx.sim.marketRate')}
          value={<span className="inline-flex flex-wrap items-center gap-1">{rate(r.marketToman)}{r.source ? <Badge variant="outline">{sourceLabel(r.source)}</Badge> : null}</span>}
        />
        <Fact
          label={t('aiPricing.fx.sim.pricingRate', { premium: pct(r.premiumPct) })}
          value={rate(r.fxToman)}
        />
        <Fact label={t('aiPricing.fx.sim.currentRate')} value={rate(r.currentFxToman)} />
        <Fact label={t('aiPricing.fx.sim.baseRate')} value={rate(r.baseFxToman)} />
        <Fact label={t('aiPricing.fx.sim.check')} value={<Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{statusLabel(r.status)}</Badge>} />
        <Fact label={t('aiPricing.fx.sim.simulated')} value={r.standIn ? t('aiPricing.fx.sim.standIn') : policyLink(r.policyId)} />
        <Fact label={t('aiPricing.fx.sim.base')} value={policyLink(r.basePolicyId)} />
        <Fact
          label={t('aiPricing.fx.sim.guards')}
          value={t('aiPricing.fx.sim.guardsValue', {
            min: pct(r.minChangePct),
            max: pct(r.maxStepPct),
          })}
        />
        <Fact
          label={t('aiPricing.fx.sim.runAt')}
          value={r.runAt ? <span {...LTR_TEXT} className="tabular-nums">{formatDigits(r.runAt)}</span> : EMPTY}
        />
        <Fact label={t('aiPricing.fx.sim.reprice')} value={r.repriceEnabled ? t('common.value.yes') : t('common.value.no')} />
      </dl>

      {r.whyHeld ? (
        <Alert variant="warning" data-testid="ai-fx-sim-why">
          <span className="font-semibold">{t('aiPricing.fx.sim.whyHeld')}</span>{' '}
          <span {...LTR_TEXT}>{r.whyHeld}</span>
        </Alert>
      ) : null}
      {r.reason ? (
        <Alert variant={r.status === 'rejected' ? 'error' : 'info'} data-testid="ai-fx-sim-reason">
          <span className="font-semibold">{t('aiPricing.fx.sim.reason')}</span>{' '}
          <span {...LTR_TEXT}>{r.reason}</span>
        </Alert>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
        <Table
          dense
          ariaLabel={t('aiPricing.fx.sim.changesAria')}
          rows={r.changes || []}
          rowKey={(c) => c.serviceKey}
          rowProps={(c) => ({ 'data-testid': `ai-fx-sim-change-${c.serviceKey}`, 'data-flagged': c.tooLarge || c.newlyNegative ? 'true' : undefined })}
          columns={[
            {
              key: 'service',
              header: t('aiPricing.fx.sim.columns.service'),
              render: (c) => (
                <div className="min-w-0">
                  <div className="truncate text-foreground">{labelOf(c.serviceKey)}</div>
                  <div className="truncate text-xs text-muted-foreground"><Code>{c.serviceKey}</Code></div>
                </div>
              ),
            },
            { key: 'unit', header: t('aiPricing.fx.sim.columns.unit'), width: '7rem', render: (c) => unitLabel(c.unit) },
            {
              key: 'move',
              header: t('aiPricing.fx.sim.columns.move', { coin: t('common.units.parseh') }),
              align: 'end',
              width: '10rem',
              render: (c) => (
                <span className="inline-flex items-center gap-1 whitespace-nowrap tabular-nums">
                  <ParsehCell value={c.fromParseh} />
                  <ArrowLeft size={13} aria-hidden style={MIRROR_ICON} />
                  <span className="sr-only">{t('aiPricing.fx.sim.to')}</span>
                  <ParsehCell value={c.toParseh} className="font-semibold" />
                </span>
              ),
            },
            { key: 'change', header: t('aiPricing.fx.sim.columns.change'), align: 'end', width: '6rem', render: (c) => <PercentCell value={c.changePct} /> },
            { key: 'margin', header: t('aiPricing.fx.sim.columns.margin'), align: 'end', width: '6rem', render: (c) => <MarginBadge value={c.marginPct} /> },
            {
              key: 'flags',
              header: t('aiPricing.fx.sim.columns.flags'),
              width: '11rem',
              render: (c) => (
                <span className="inline-flex flex-wrap gap-1">
                  {c.tooLarge ? <Badge variant="warning" data-testid={`ai-fx-sim-change-${c.serviceKey}-too-large`}>{t('aiPricing.fx.sim.tooLarge')}</Badge> : null}
                  {c.newlyNegative ? <Badge variant="destructive" data-testid={`ai-fx-sim-change-${c.serviceKey}-negative`}>{t('aiPricing.fx.sim.newlyNegative')}</Badge> : null}
                  {!c.tooLarge && !c.newlyNegative ? <span className="text-muted-foreground">{EMPTY}</span> : null}
                </span>
              ),
            },
          ]}
          empty={<p className="p-4 text-sm text-muted-foreground" data-testid="ai-fx-sim-changes-empty">{t('aiPricing.fx.sim.noChanges')}</p>}
          data-testid="ai-fx-sim-changes"
        />
      </div>
    </div>
  );
}

/** Every night's read of the dollar, newest first, server-paged. */
function FxHistory({ go }) {
  const { t, formatNumber, formatDate, formatDateTime, formatDigits } = useTranslation();
  const outcomeLabel = useEnumLabel('aiPricing.fx.outcome');
  const statusLabel = useEnumLabel('aiPricing.fx.status');
  const sourceLabel = useEnumLabel('aiPricing.fx.source');
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    gql(FX_RATES_QUERY, { page, pageSize: FX_PAGE_SIZE })
      .then((d) => setData(d?.aiFxRates || { rates: [], total: 0 }))
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [page, t]);

  React.useEffect(() => { load(); }, [load]);

  const rate = (v) => (num(v) == null ? EMPTY : <TomanCell value={v} />);
  const rows = data?.rates || [];

  return (
    <div data-testid="ai-fx-history" data-state={loading ? 'loading' : error ? 'error' : rows.length ? 'content' : 'empty'}>
      <h3 className="mb-2 inline-flex items-center gap-1.5 text-sm font-bold text-foreground">
        <History size={15} aria-hidden />
        {t('aiPricing.fx.history.title')}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-border">
        {loading ? (
          <TableSkeleton cols={7} rows={4} label={t('aiPricing.fx.history.loading')} data-testid="ai-fx-history-loading" />
        ) : error ? (
          <ErrorState
            title={t('aiPricing.fx.history.error')}
            description={error}
            onRetry={load}
            retryLabel={t('common.actions.retry')}
            data-testid="ai-fx-history-error"
          />
        ) : (
          <Table
            dense
            ariaLabel={t('aiPricing.fx.history.aria')}
            rows={rows}
            rowKey={(r) => r.id}
            rowProps={(r) => ({ 'data-testid': `ai-fx-history-row-${r.id}` })}
            columns={[
              {
                key: 'date',
                header: t('aiPricing.fx.history.columns.date'),
                width: '11rem',
                render: (r) => (
                  <span title={r.fetchedAt ? formatDateTime(r.fetchedAt) : undefined}>
                    {/^\d{4}-\d{2}-\d{2}$/.test(r.rateDate || '') ? formatDate(`${r.rateDate}T12:00:00Z`) : EMPTY}
                  </span>
                ),
              },
              {
                key: 'market',
                header: t('aiPricing.fx.history.columns.market', { unit: t('common.units.toman') }),
                align: 'end',
                width: '9rem',
                render: (r) => (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    {rate(r.marketTomanPerUsd)}
                    {r.source ? <span className="text-[11px] text-muted-foreground">{sourceLabel(r.source)}</span> : null}
                  </span>
                ),
              },
              {
                key: 'pricing',
                header: t('aiPricing.fx.history.columns.pricing', { unit: t('common.units.toman') }),
                align: 'end',
                width: '9rem',
                render: (r) => (
                  <span className="inline-flex flex-col items-end gap-0.5">
                    {rate(r.fxTomanPerUsd)}
                    {num(r.premiumPct) != null ? (
                      <span className="text-[11px] text-muted-foreground">{t('aiPricing.fx.history.premium', { pct: formatNumber(num(r.premiumPct), { maximumFractionDigits: 2 }) })}</span>
                    ) : null}
                  </span>
                ),
              },
              { key: 'status', header: t('aiPricing.fx.history.columns.status'), width: '7rem', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'outline'}>{statusLabel(r.status)}</Badge> },
              { key: 'outcome', header: t('aiPricing.fx.history.columns.outcome'), width: '8rem', render: (r) => <Badge variant={OUTCOME_VARIANT[r.outcome] || 'outline'}>{outcomeLabel(r.outcome)}</Badge> },
              {
                key: 'reason',
                header: t('aiPricing.fx.history.columns.reason'),
                render: (r) => (r.reason ? <span {...LTR_TEXT} className="block max-w-[28rem] truncate text-xs" title={r.reason}>{r.reason}</span> : EMPTY),
              },
              {
                key: 'policy',
                header: t('aiPricing.fx.history.columns.policy'),
                width: '8rem',
                render: (r) => (r.policyId ? (
                  <Button variant="link" size="sm" className="h-auto p-0" onClick={() => go({ policy: String(r.policyId) })} data-testid={`ai-fx-history-row-${r.id}-policy`}>
                    {t('aiPricing.services.source.policy', { id: formatDigits(r.policyId) })}
                  </Button>
                ) : EMPTY),
              },
            ]}
            empty={(
              <EmptyState
                icon={<History size={44} strokeWidth={1.5} aria-hidden />}
                title={t('aiPricing.fx.history.empty.title')}
                description={t('aiPricing.fx.history.empty.desc')}
                data-testid="ai-fx-history-empty"
              />
            )}
            data-testid="ai-fx-history-table"
          />
        )}
      </div>
      {!loading && !error && (data?.total || 0) > FX_PAGE_SIZE ? (
        <div data-testid="ai-fx-history-pager">
          <Pager
            page={page}
            pageCount={pageCountOf(data?.total, FX_PAGE_SIZE)}
            pageSize={FX_PAGE_SIZE}
            onChange={setPage}
            prevLabel={t('common.pager.prev')}
            nextLabel={t('common.pager.next')}
            pageLabel={(p, total) => t('common.pager.label', { page: p, total })}
            formatNumber={formatNumber}
          />
        </div>
      ) : null}
    </div>
  );
}
